import type { Actor } from './actor';
import type { ResolvedSkill } from './skillTypes';
import type { World } from './world';

/**
 * Passive effects (sigils, set bonuses, cards) that need runtime context the
 * static stat block can't express, e.g. "+20% damage to bosses" or "stacks on
 * every skill cast". Actors carry `{ id, level }` entries; hooks are looked up
 * here so the data stays serialisable.
 */
export interface PassiveContext {
  src: Actor;
  target: Actor;
  skill: ResolvedSkill | null;
  back: boolean;
  head: boolean;
  crit: boolean;
}

export interface PassiveHooks {
  /** Multiplier on outgoing damage. */
  outDamage?(ctx: PassiveContext): number;
  /** Additive crit rate before the crit roll. */
  critRate?(src: Actor, target: Actor, skill: ResolvedSkill | null): number;
  /** Additive crit damage multiplier. */
  critDmg?(src: Actor, skill: ResolvedSkill | null): number;
  /** Multiplier on incoming damage (on the defender). */
  inDamage?(target: Actor, src: Actor | null): number;
  /** Called on the owner's peer when the actor casts a skill. */
  onCast?(world: World, actor: Actor, skill: ResolvedSkill): void;
  /** Multiplier on a skill's cooldown when resolved. */
  cooldownMult?(skill: ResolvedSkill): number;
  /** Extra stagger multiplier. */
  staggerMult?(): number;
}

export interface PassiveRef {
  id: string;
  level: number;
}

const PASSIVES = new Map<string, (level: number) => PassiveHooks>();

export function registerPassive(id: string, make: (level: number) => PassiveHooks): void {
  PASSIVES.set(id, make);
}

const cache = new Map<string, PassiveHooks>();
export function passiveHooks(ref: PassiveRef): PassiveHooks | null {
  const key = `${ref.id}:${ref.level}`;
  let h = cache.get(key);
  if (!h) {
    const make = PASSIVES.get(ref.id);
    if (!make) return null;
    h = make(ref.level);
    cache.set(key, h);
  }
  return h;
}

export function forEachPassive(a: Actor, fn: (h: PassiveHooks) => void): void {
  for (const p of a.passives) {
    const h = passiveHooks(p);
    if (h) fn(h);
  }
}
